import { Grid } from "@mui/material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  useGetQuestionnaireContentQuery,
  useGetUserAnswersQuery,
} from "api/useQuestionnaireQueries";
import StatusIndicator from "components/lib/statusIndicator";
import { AnswerOption } from "core/types";
import { Typography } from "components/typography";
import { useQuestionnaireScores } from "../hooks";
import {
  QuestionnaireContainer,
  QuestionnaireContent as QuestionnaireContentContainer,
  QuestionnaireScoringArea,
  QuestionnaireTextArea,
} from "./modal.styles";
import { mergeContentWithAnswers } from "./mergeContentWithAnswers";
import { Divider, Spacing, Spinner } from "@mindlercare/ui-web";

export const QuestionnaireContent = ({
  clinicalId,
  requestId,
  patientUserId,
  hideUnansweredScoreArea,
  hideAnsweredScoreArea,
  questionnaireType,
}: {
  clinicalId?: string;
  requestId?: string;
  patientUserId?: number;
  hideUnansweredScoreArea?: boolean;
  hideAnsweredScoreArea?: boolean;
  questionnaireType: "clinical" | "icbt";
}) => {
  const { t } = useTranslation();
  const { data: questionnaireContent, isFetching: contentPending } =
    useGetQuestionnaireContentQuery(clinicalId);

  const { data: questionnaireAnswers } = useGetUserAnswersQuery({
    userId: patientUserId?.toString(),
    requestId,
    answerRequestType: questionnaireType,
  });

  const userAnswers = questionnaireAnswers ?? [];

  const questionnaire = mergeContentWithAnswers(
    requestId,
    questionnaireContent,
    userAnswers,
  );

  const hasAnswers = questionnaire?.questions.find(
    (question) => !!question.userAnswer,
  );

  const { resultScore, maxScore } = useQuestionnaireScores(
    questionnaire,
    userAnswers,
  );

  const sortedScoreSteps = [...(questionnaire?.scoreSteps || [])].sort(
    (a, b) => a.boundary - b.boundary,
  );

  const hideScoreArea =
    (hasAnswers && hideAnsweredScoreArea) ||
    (!hasAnswers && hideUnansweredScoreArea);
  return (
    <>
      {contentPending ? (
        <Spinner />
      ) : (
        <QuestionnaireContainer>
          {hideScoreArea ? null : (
            <QuestionnaireScoringArea>
              <Typography variant="h2">
                {`${t("PATIENT_QUESTIONNAIRE_TABLE_HEADER_SCORE")}: ${
                  resultScore ?? "--"
                }/${maxScore ?? "--"} - ${
                  sortedScoreSteps.find(
                    (step) => (resultScore || 0) <= step.boundary,
                  )?.stepText
                }`}
              </Typography>
              <Spacing b={12} />

              <Typography insensitive variant="h5" color="GRAY_DARK">
                {questionnaire?.description ?? "--"}
              </Typography>
              <Spacing b={12} />

              <Grid container direction="column">
                {sortedScoreSteps.map((step, i) => {
                  const lowerBound =
                    i > 0 ? sortedScoreSteps[i - 1].boundary + 1 : 0;
                  const upperBound = step.boundary;
                  const bold = !!(
                    resultScore &&
                    resultScore >= lowerBound &&
                    resultScore <= upperBound
                  );
                  return (
                    <Grid item key={step.stepText}>
                      <Spacing t={2} />
                      <Typography
                        variant={bold ? "body-small-bold" : "body-small"}
                        color="GRAY_DARK"
                        key={step.stepText}
                      >
                        {`${lowerBound} - ${upperBound ?? "--"} = ${
                          step.stepText ?? "--"
                        }`}
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
            </QuestionnaireScoringArea>
          )}
          <QuestionnaireContentContainer>
            <QuestionnaireTextArea data-testid="qwModalBody">
              <Typography variant="h3">
                {questionnaire?.topLevelQuestion ?? "--"}
              </Typography>
              <Spacing b={16} />
              {questionnaire?.questions.map((question, i) => {
                if (question.type === "SELECT_ONE") {
                  return (
                    <UserAnswerItem
                      key={question.questionId}
                      questionId={question.questionId}
                      questionName={question.name}
                      userAnswer={question.userAnswer}
                      questionIndex={i + 1}
                    />
                  );
                }

                return null;
              })}
            </QuestionnaireTextArea>
          </QuestionnaireContentContainer>
        </QuestionnaireContainer>
      )}
    </>
  );
};

const UserAnswerItem = ({
  questionId,
  questionName,
  userAnswer,
  questionIndex,
}: {
  questionId: string;
  questionIndex: number;
  questionName?: string;
  userAnswer?: AnswerOption;
}) => {
  const { t } = useTranslation();
  const { score, name, statusColor } = userAnswer ?? {
    score: undefined,
    name: undefined,
    statusColor: undefined,
  };

  return (
    <Fragment>
      <Grid container wrap="nowrap">
        <Spacing l={16} />
        <Grid item container direction="column" wrap="nowrap">
          <Typography variant="h5">
            {questionIndex}. {questionName ?? "--"}
          </Typography>
          <Grid item container alignItems="center">
            {!name && "-"}
            {name && (
              <>
                {statusColor?.name && (
                  <>
                    <StatusIndicator
                      data-testid="test-statusIndicator-primary"
                      variant={
                        statusColor?.name
                          ? `STATUS_${statusColor?.name}`
                          : "primary"
                      }
                      key={`${questionId}-status`}
                    />
                  </>
                )}

                <Typography variant="body-small" color="GRAY_DARK">
                  {name},
                </Typography>
                <Spacing r={3} />

                <Typography variant="body-small" color="GRAY">
                  {score || score === 0
                    ? `+ ${score}${t("POINTS_ABBREVIATION")}`
                    : ""}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
        <Spacing r={16} />
      </Grid>

      <Spacing b={16} />
      <Divider />
      <Spacing b={16} />
    </Fragment>
  );
};
